import { LoaderIcon, Sliders, Triangle, X } from "lucide-react";
import { useRouter } from "next/router";
import { Dropdown } from "@/atoms/Dropdown/Dropdown";
import routes from "@/constants/routes";
import { useQuery } from "react-query";
import { getDevelopementsDropdowns } from "@/services/cms/api";
import PinHollow from "@/public/icons/pin-orange-hollow.svg";

export type ParkingType = "Yes" | "No" | null;

interface LocationFilterBarProps {
  listingType: "map" | "list";
  setListingType: (v: "map" | "list") => void;

  sortField: string | null;
  setSortField: (v: string | null) => void;

  sortDirection: "asc" | "desc" | null;
  setSortDirection: (v: "asc" | "desc" | null) => void;

  openSortAndFilter: "sort" | "filter" | null;
  setOpenSortAndFilter: (v: "sort" | "filter" | null) => void;

  bedrooms: number | null;
  setBedrooms: (v: number | null) => void;

  bathrooms: number | null;
  setBathrooms: (v: number | null) => void;

  availability: "available" | "unavailable" | null;
  setAvailability: (v: "available" | "unavailable" | null) => void;

  searchTerm?: string | null;

  parkingSpace: ParkingType;
  setParkingSpace: (v: ParkingType) => void;
}

const LocationFilterBar = (props: LocationFilterBarProps) => {
  const {
    listingType,
    setListingType,
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
    openSortAndFilter,
    setOpenSortAndFilter,
    bedrooms,
    setBedrooms,
    bathrooms,
    setBathrooms,
    availability,
    setAvailability,
    searchTerm,
    parkingSpace,
    setParkingSpace,
  } = props;

  const router = useRouter();

  const { data: dropdownData } = useQuery("developments-dropdowns", getDevelopementsDropdowns, {
    staleTime: Infinity,
    cacheTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const FILTER_PARAMS = ["beds", "baths", "parking", "availability", "sort", "dir"] as const;

  const toggleSort = () => {
    setOpenSortAndFilter(openSortAndFilter === "sort" ? null : "sort");
  };

  const toggleFilter = () => {
    setOpenSortAndFilter(openSortAndFilter === "filter" ? null : "filter");
  };

  const noOfFiltersApplied =
    (bedrooms ? 1 : 0) + (bathrooms ? 1 : 0) + (availability ? 1 : 0) + (parkingSpace ? 1 : 0);

  const getSortName = () => {
    if (sortField === "futureBuyBackPrice") {
      return sortDirection === "asc" ? "Lowest price" : "Highest price";
    } else if (sortField === "addedOn") {
      return sortDirection === "asc" ? "Least recently listed" : "Recently listed";
    } else {
      return "";
    }
  };

  const onSelectLocation = (locationId: string | null) => {
    const filterQuery = Object.fromEntries(
      FILTER_PARAMS.filter((k) => typeof router.query[k] === "string").map((k) => [
        k,
        router.query[k],
      ]),
    );

    if (locationId === null) {
      router.push({ pathname: routes.searchHomes, query: filterQuery });
    } else {
      router.push({
        pathname: routes.properties[locationId as keyof typeof routes.properties],
        query: filterQuery,
      });
    }
  };

  return (
    <div className="relative flex flex-col items-center justify-center gap-4 mt-4 md:mt-0 mb-8 xl:max-w-[1440px] mx-auto">
      <p className="self-center">{searchTerm ? `Showing homes in...` : `Find your ideal home`}</p>

      <div className="flex justify-between md:justify-center w-full">
        <div className=" w-[267px] justify-center bg-white border border-landing-orange rounded-full hidden xl:flex left-0 absolute ml-5">
          <div
            className={`${
              listingType === "list" ? "bg-landing-orange" : ""
            } p-3 rounded-full cursor-pointer flex items-center justify-center h-[66px] w-1/2`}
            onClick={() => setListingType("list")}
          >
            <p
              className={
                listingType === "list"
                  ? "font-semibold text-lg text-white"
                  : "text-landing-blue text-lg"
              }
            >
              List homes
            </p>
          </div>

          <div
            className={`${
              listingType === "map" ? "bg-landing-orange" : ""
            } p-3 rounded-full cursor-pointer flex items-center justify-center h-[66px] w-1/2`}
            onClick={() => setListingType("map")}
          >
            <p
              className={
                listingType === "map"
                  ? "font-semibold text-lg text-white"
                  : "text-landing-blue text-lg font-normal"
              }
            >
              Map view
            </p>
          </div>
        </div>

        <div className=" flex-wrap items-center justify-center w-full max-w-[434px]   relative mx-auto flex ">
          {dropdownData ? (
            <Dropdown
              LeftIcon={<PinHollow className=" fill-landing-orange" />}
              title="Select a Keyzy Development"
              data={[
                ...dropdownData,
                { key: null, heading: "All Keyzy homes", subHeading: "London" },
              ]}
              selectedData={searchTerm}
              setSelectedData={onSelectLocation}
            />
          ) : (
            <div className="w-full flex items-center justify-center border border-landing-orange rounded-full h-[66px]">
              <LoaderIcon className="animate-spin" stroke="#f39237" />
            </div>
          )}
        </div>

        <div className="hidden xl:flex flex-col absolute right-0 z-40 mr-5 ">
          <div
            className={` flex  ${
              listingType === "list" ? "justify-between" : "justify-end"
            } gap-[12px]`}
          >
            {listingType === "list" && (
              <button
                className={` h-[66px] flex items-center gap-2 w-[145px] py-[5px] justify-center rounded-full text-lg border border-landing-orange ${
                  sortField === null ? "bg-white  " : "bg-landing-orange font-bold text-white"
                }`}
                onClick={toggleSort}
              >
                <p>Sort</p>
                <div>
                  <Triangle
                    className="my-1 h-[12px] w-[12px]"
                    stroke="null"
                    fill={sortField ? "#FFF" : "#f39237"}
                  />
                  <Triangle
                    className="my-1 h-[12px] w-[12px] rotate-180"
                    stroke="null"
                    fill={sortField ? "#FFF" : "#f39237"}
                  />
                </div>
              </button>
            )}

            <button
              className={`  h-[66px] flex items-center gap-2 w-[145px] justify-center rounded-full text-lg border border-landing-orange ${
                bathrooms || bedrooms || availability || parkingSpace
                  ? "bg-landing-orange font-bold text-white"
                  : "bg-white"
              }`}
              onClick={toggleFilter}
            >
              <p>{noOfFiltersApplied > 0 && `(${noOfFiltersApplied}) `}Filter</p>
              <Sliders
                stroke={bathrooms || bedrooms || availability || parkingSpace ? "#FFF" : "#f39237"}
              />
            </button>
          </div>

          {openSortAndFilter === "sort" ? (
            <SortPanel
              sortField={sortField}
              sortDirection={sortDirection}
              setSortField={setSortField}
              setSortDirection={setSortDirection}
              onClose={() => setOpenSortAndFilter(null)}
            />
          ) : (
            <div className="w-[130px] mt-1 text-center text-landing-orange leading-none">
              {getSortName()}
            </div>
          )}

          {openSortAndFilter === "filter" && (
            <FilterPanel
              bedrooms={bedrooms}
              setBedrooms={setBedrooms}
              bathrooms={bathrooms}
              setBathrooms={setBathrooms}
              availability={availability}
              setAvailability={setAvailability}
              parkingSpace={parkingSpace}
              setParkingSpace={setParkingSpace}
              onClose={() => setOpenSortAndFilter(null)}
              listingType={listingType}
            />
          )}
        </div>
      </div>

      {/* MobileView */}
      <div className=" justify-center w-full max-w-[434px] bg-white border border-landing-orange rounded-full flex xl:hidden left-0  text-center items-center">
        <div
          className={`${
            listingType === "list" ? "bg-landing-orange" : ""
          } p-3 rounded-full cursor-pointer w-1/2 h-[66px] flex items-center justify-center`}
          onClick={() => setListingType("list")}
        >
          <p
            className={
              listingType === "list"
                ? "font-semibold text-lg text-white"
                : "text-landing-blue text-lg"
            }
          >
            List homes
          </p>
        </div>

        <div
          className={`${
            listingType === "map" ? "bg-landing-orange" : ""
          }  p-3 rounded-full cursor-pointer w-1/2  h-[66px] justify-center flex items-center`}
          onClick={() => setListingType("map")}
        >
          <p
            className={
              listingType === "map"
                ? "font-semibold text-lg text-white"
                : "text-landing-blue text-lg"
            }
          >
            Map view
          </p>
        </div>
      </div>

      <div className="flex xl:hidden flex-col z-40 self-center w-full max-w-[434px]">
        <div
          className={`flex ${
            listingType === "list" ? "justify-between " : "justify-center"
          } gap-[12px] w-full max-w-[434px]`}
        >
          {listingType === "list" && (
            <button
              className={`h-[66px] flex items-center gap-2  w-1/2 max-w-[210px] border border-landing-orange justify-center py-[5px] rounded-full text-lg ${
                sortField === null ? "bg-white" : "bg-landing-orange font-bold text-white"
              }`}
              onClick={toggleSort}
            >
              <p>Sort</p>
              <div>
                <Triangle
                  className="my-1 h-[12px] w-[12px]"
                  stroke="null"
                  fill={sortField ? "#FFF" : "#F39237"}
                />
                <Triangle
                  className="my-1 h-[12px] w-[12px] rotate-180"
                  stroke="null"
                  fill={sortField ? "#FFF" : "#F39237"}
                />
              </div>
            </button>
          )}

          <button
            className={`flex items-center gap-2  w-1/2 max-w-[210px] justify-center py-[5px] rounded-full text-lg text-black-80 border border-landing-orange ${
              bathrooms || bedrooms || availability
                ? "bg-landing-orange font-bold text-white"
                : "bg-white"
            } h-[66px] `}
            onClick={toggleFilter}
          >
            <p>{noOfFiltersApplied > 0 && `(${noOfFiltersApplied}) `}Filter</p>
            <Sliders stroke={bathrooms || bedrooms || availability ? "#FFF" : "#F39237"} />
          </button>
        </div>
        <div className="relative">
          {openSortAndFilter === "sort" ? (
            <SortPanel
              sortField={sortField}
              sortDirection={sortDirection}
              setSortField={setSortField}
              setSortDirection={setSortDirection}
              onClose={() => setOpenSortAndFilter(null)}
            />
          ) : (
            <div className="w-[130px] mt-1 text-center text-landing-orange leading-none">
              {getSortName()}
            </div>
          )}

          {openSortAndFilter === "filter" && (
            <FilterPanel
              bedrooms={bedrooms}
              setBedrooms={setBedrooms}
              bathrooms={bathrooms}
              setBathrooms={setBathrooms}
              availability={availability}
              setAvailability={setAvailability}
              parkingSpace={parkingSpace}
              setParkingSpace={setParkingSpace}
              onClose={() => setOpenSortAndFilter(null)}
              listingType={listingType}
            />
          )}
        </div>
      </div>
    </div>
  );
};

function SortPanel({
  sortField,
  sortDirection,
  setSortField,
  setSortDirection,
  onClose,
}: {
  sortField: string | null;
  sortDirection: "asc" | "desc" | null;
  setSortField: (v: string | null) => void;
  setSortDirection: (v: "asc" | "desc" | null) => void;
  onClose: () => void;
}) {
  const handleSort = (field: string, direction: "asc" | "desc") => {
    if (sortField === field && sortDirection === direction) {
      setSortField(null);
      setSortDirection(null);
    } else {
      setSortField(field);
      setSortDirection(direction);
    }
  };

  return (
    <div className="bg-white border border-landing-orange p-[18px] rounded-[35px] w-[260px] mt-4 text-left absolute xl:relative xl:w-auto shadow-md">
      <div className="justify-between flex items-center">
        <div className="flex items-center gap-2">
          <p className="text-landing-blue text-lg">Sort</p>
          <div>
            <Triangle className="my-1 h-[12px] w-[12px]" stroke="null" fill={"#F39237"} />
            <Triangle
              className="my-1 h-[12px] w-[12px] rotate-180"
              stroke="null"
              fill={"#F39237"}
            />
          </div>
        </div>
        <X color="#F39237" onClick={onClose} className="cursor-pointer" />
      </div>

      <ul className="mt-2">
        <li
          className={`py-2 text-lg text-landing-blue ${
            sortField === "futureBuyBackPrice" && sortDirection === "desc" ? "font-bold" : ""
          }`}
        >
          <button onClick={() => handleSort("futureBuyBackPrice", "desc")}>Highest price</button>
        </li>

        <li
          className={`py-2 text-lg text-landing-blue ${
            sortField === "futureBuyBackPrice" && sortDirection === "asc" ? "font-bold" : ""
          }`}
        >
          <button onClick={() => handleSort("futureBuyBackPrice", "asc")}>Lowest price</button>
        </li>

        <li
          className={`py-2 text-lg text-landing-blue ${
            sortField === "addedOn" && sortDirection === "desc" ? "font-bold" : ""
          }`}
        >
          <button onClick={() => handleSort("addedOn", "desc")}>Recently listed</button>
        </li>

        <li
          className={`py-2 text-lg text-landing-blue ${
            sortField === "addedOn" && sortDirection === "asc" ? "font-bold" : ""
          }`}
        >
          <button onClick={() => handleSort("addedOn", "asc")}>Least recently listed</button>
        </li>
      </ul>
    </div>
  );
}

function FilterPanel({
  bedrooms,
  setBedrooms,
  bathrooms,
  setBathrooms,
  availability,
  setAvailability,
  parkingSpace,
  setParkingSpace,
  onClose,
  listingType,
}: {
  bedrooms: number | null;
  setBedrooms: (v: number | null) => void;
  bathrooms: number | null;
  setBathrooms: (v: number | null) => void;
  availability: string | null;
  setAvailability: (v: "available" | "unavailable" | null) => void;
  parkingSpace: ParkingType;
  setParkingSpace: (v: ParkingType) => void;
  onClose: () => void;
  listingType: "map" | "list";
}) {
  const toggleNumber = (
    current: number | null,
    value: number,
    setter: (v: number | null) => void,
  ) => {
    setter(current === value ? null : value);
  };

  const toggleAvailability = (value: "available" | "unavailable") => {
    setAvailability(availability === value ? null : value);
  };

  const toggleParking = (value: ParkingType) => {
    setParkingSpace(parkingSpace === value ? null : value);
  };

  return (
    <div className="bg-white border border-landing-orange p-[18px] rounded-[35px] w-[303px] mt-4 text-left flex flex-col absolute xl:relative xl:w-auto gap-[15px] shadow-md right-0">
      <div className="justify-between flex items-center">
        <X color="#F39237" onClick={onClose} className="cursor-pointer" />
        <div className="flex items-center gap-2">
          <p className="text-landing-blue text-lg">Filter</p>
          <Sliders stroke={"#F39237"} />
        </div>
      </div>

      <p className="font-bold text-landing-blue">Bedrooms</p>
      <ul className="flex gap-[19px]">
        {[1, 2, 3].map((num) => (
          <li
            key={num}
            className={`cursor-pointer py-2 font-normal text-lg text-black-80 border w-[60px] h-[55px] flex items-center justify-center rounded-[8px] border-landing-orange ${
              bedrooms === num ? "font-bold text-white bg-landing-orange" : ""
            }`}
            onClick={() => toggleNumber(bedrooms, num, setBedrooms)}
          >
            {num}
          </li>
        ))}
      </ul>

      <p className="font-bold text-landing-blue">Bathrooms</p>
      <ul className=" flex gap-[19px]">
        {[1, 2, 3].map((num) => (
          <li
            key={num}
            className={`cursor-pointer py-2 font-normal text-lg text-black-80 border w-[60px] h-[55px] flex items-center justify-center rounded-[8px] border-landing-orange ${
              bathrooms === num ? "font-bold text-white bg-landing-orange" : ""
            }`}
            onClick={() => toggleNumber(bathrooms, num, setBathrooms)}
          >
            {num}
          </li>
        ))}
      </ul>
      {listingType !== "map" && (
        <>
          <p className="font-bold text-landing-blue ">Availability</p>
          <div
            className={`cursor-pointer self-start flex py-2  px-5 font-normal text-lg text-black-80 border items-center justify-center rounded-[8px] border-landing-orange ${
              availability === "available" ? "font-bold text-white bg-landing-orange" : ""
            }`}
            onClick={() => toggleAvailability("available")}
          >
            Available now
          </div>

          <div
            className={`cursor-pointer self-start  flex py-2 px-5 font-normal text-lg text-black-80 border items-center justify-center rounded-[8px] border-landing-orange ${
              availability === "unavailable" ? "font-bold text-white bg-landing-orange" : ""
            }`}
            onClick={() => toggleAvailability("unavailable")}
          >
            Reserved / Coming soon
          </div>
        </>
      )}

      <p className="font-bold text-landing-blue">Parking space</p>
      <ul className="flex gap-[19px] mb-3">
        {["Yes", "No"].map((value) => (
          <li
            key={value}
            className={`cursor-pointer py-2 font-normal text-lg text-black-80 border w-[60px] h-[55px] flex items-center justify-center rounded-[8px] border-landing-orange ${
              parkingSpace === value ? "font-bold text-white bg-landing-orange" : ""
            }`}
            onClick={() => toggleParking(value as ParkingType)}
          >
            {value}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default LocationFilterBar;
